import { ColorContext } from 'App';
import { PageNumber } from 'components/index';
import SearchBar from 'components/SearchBar/SearchBar';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { PermsContext } from 'contexts/PermsContext';
import useSearch from 'hooks/useSearch';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { getAuditLogs } from 'services/organization/organization.service';
import AuditLogDisplay from './AuditLogDisplay';

let logsCache = {};
let maxPagesCache = {};

export default function AuditLogs() {
  const [query_temp, setQuery_Temp] = useState('');
  const [pageIndex, setPageIndex] = useState(0); //current page index (0-based)
  const [PAGE_LENGTH, setPAGE_LENGTH] = useState(10); //number of logs per page
  const PAGE_LENGTHS = [10, 25, 50, 100];
  const [maxPages, setMaxPages] = useState(1); // max pages for pagination, gets told to us by server
  const [sortOrder, setSortOrder] = useState(-1); // 1 for ascending, -1 for descending
  const [sortKey, setSortKey] = useState('log_id');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [logs, setLogs] = useState([]);
  const [canViewLogs, setCanViewLogs] = useState(false);
  const perms = useContext(PermsContext);
  const params = useSearch(2000);

  const grabLogs = useCallback(async () => {
    //Check perms first, if no perms the audit log does not even attempt to fetch logs. Disables audit log viewing.
    if (perms && !perms.testPermission([8, 11, 12, 13], ['ViewAuditLog'])) {
      return { entries: [], max_pages: 1 };
    } else {
      setCanViewLogs(true);
      const res = await getAuditLogs(pageIndex, PAGE_LENGTH, params, sortKey, sortOrder);
      return res.data.result;
    }
  }, [PAGE_LENGTH, pageIndex, params, perms, sortKey, sortOrder]);

  const getLogs = useCallback(async () => {
    setSelectedIndex(-1);

    const cacheKey = `query:"${params}"-page:${pageIndex}-pageLength:${PAGE_LENGTH}-sortKey:${sortKey}-sortOrder:${sortOrder}`;

    if (logsCache[cacheKey]) {
      setLogs(logsCache[cacheKey]);
      setMaxPages(maxPagesCache[cacheKey]);
    } else {
      const res = await grabLogs();

      setMaxPages(res.max_pages || 1);
      maxPagesCache[cacheKey] = res.max_pages || 1;
      setLogs(res.entries);
      logsCache[cacheKey] = res.entries;
    }
  }, [PAGE_LENGTH, pageIndex, params, sortKey, sortOrder, grabLogs]);

  //get logs on page load, index change, sort change, page length change
  useEffect(() => {
    getLogs();
  }, [getLogs]);

  // Reset to first page when filters change (PAGE_LENGTH, search params, or perms)
  useEffect(() => {
    setPageIndex(0);
  }, [PAGE_LENGTH, params, perms]);

  const theme = useContext(ColorContext);

  if (!logs) return <WidgetLoadingAnimation />;

  return (
    <Container fluid className="h-100 d-flex flex-column min-h-0 mt-3 py-3 overflow-hidden">
      {/* Header */}
      <div className="d-flex flex-column justify-content-between align-items-center mb-4 flex-shrink-0">
        <div className="flex-row h2 justify-content-center w-100 over">Audit Logs</div>

        <div className="w-100 w-lg-25 mb-2 justify-content-end ms-auto">
          <SearchBar
            className="bg-primary rounded-3 w-100"
            placeholder="Search Logs"
            value={query_temp}
            onChange={(e) => setQuery_Temp(e.target.value)}
          />
        </div>
      </div>

      {/* Content (scrollable) */}
      <div className="flex-grow-1 min-h-0 overflow-hidden">
        {canViewLogs ? (
          <AuditLogDisplay
            list={logs}
            setSelectedIndex={setSelectedIndex}
            selectedIndex={selectedIndex}
            setSortKey={setSortKey}
            sortKey={sortKey}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
          />
        ) : (
          <div className="text-center fw-bold">You do not have permission to view audit logs.</div>
        )}
      </div>

      {/* Footer */}
      <div className="d-flex flex-lg-row flex-column justify-content-between align-items-center pt-3 flex-shrink-0">
        <div />
        <PageNumber value={pageIndex} onChange={setPageIndex} pages={maxPages} />
        <div>
          Page length:
          {PAGE_LENGTHS.map((length) => (
            <Button
              key={length}
              className="cursor-pointer rounded-1 m-1 p-1"
              style={{
                background: length === PAGE_LENGTH ? theme.primaryHighlight : theme.base,
                color: theme.fontColor,
              }}
              onClick={() => setPAGE_LENGTH(length)}
            >
              {length}
            </Button>
          ))}
        </div>
      </div>
    </Container>
  );
}
